import { Box, Button, Card, CardContent, FormHelperText, Grid, useMediaQuery, SwipeableDrawer, MenuItem, 
    CardHeader, Autocomplete, Stack, FormControl, InputLabel, OutlinedInput, Popover, Typography, Link, Select, Chip, FormControlLabel, Checkbox } from "@mui/material";
import { useFormik, useFormikContext } from "formik";
import { ReduxState, useListContext } from "ra-core";
import { ChangeEvent, FC, ReactElement, useMemo, useState } from "react";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import Create from "~/controller/Create";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import { FileTemplateKind, FileEncryptionTemplateDto } from "~/gen/api/encrypt";
import SelectField from "../formik/SelectField";
import { useSelector } from "react-redux";


const validationSchema =
  Yup.object().shape({
    name: Yup.string().max(30).required('Template Name is required'),
    kind: Yup.number().required('Template Type is required'),
    templateFile: Yup.string().max(255).required('Template File is required'),
  })

const maxFileSize = 256 * 1024 // 256KB

const resource = "file-encrypt-template"

interface UploadFileTemplateProps {
  open: boolean;
  setOpen: (x: boolean) => void;
}

const UploadFileTemplate: FC<UploadFileTemplateProps> = (props) => {
  const handlers = useDefaultSavedHandlers("Upload File Template")
  return (
    <Create resource={resource} basePath="" {...handlers}>
      <UploadFileTemplateBody {...props} />
    </Create>
  )
}

const UploadFileTemplateBody: FC<UploadFileTemplateProps> = ({ open, setOpen }) => {
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const save = useSimpleSaveAction()
  const initialValues = {
    submit: '',
    name: '',
    kind: FileTemplateKind.FixedWidth,
    delimitingChar: ',',
    escapeChar: ['"'],
    templateFile: '',
    tFile: null as File|null,
    copyFrom: '' as number|'',
    includeObfs: false
  }
  const [states, setStates] = useState({
    prevFileSelection: null as string|null,
    exampleAnchor: null as any,
    copyMapping: false,
  })
  const { refetch } = useListContext()

  const validate = (values: typeof initialValues) => {
    const error = {} as any
    if (values.templateFile) {
      if (states.prevFileSelection === values.templateFile) {
        error['templateFile'] = 'Same file selected as last upload. If you mean to select this file, clear selection and choose again.'
      } else if (values.tFile && values.tFile.size && values.tFile.size > maxFileSize) {
        error['templateFile'] = 'File size exceeds max size of 256KB'
      }
    }
    if (Object.keys(error).length > 0) {
      return error
    }
  }

  const onSubmit: OnSubmit<typeof initialValues> = (values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    if (values.tFile === null || values.templateFile === states.prevFileSelection) { return }
    setStates(v => ({...v, prevFileSelection: values.templateFile }))
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
        refetch()
        handleClose()
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }
    const payload = {...values, 
      delimitingChar: values.delimitingChar || ',',
      escapeChar: values.escapeChar.pop() || '"',
      templateFile: values.tFile,
      tFile: undefined,
    }
    save(payload, options)
  }

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, validate })
  const { errors, values, touched, isSubmitting, resetForm, setValues, setFieldTouched, setFieldValue } = formikCtrl

  const handleTemplateFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) { 
      setStates(v => ({...v, prevFileSelection: null }))
    }
    setFieldTouched('templateFile', true)
    setValues(v => ({...v, 
      tFile: e.target.files ? (e.target.files[0] || null) : null,
      templateFile: e.target.value,
    }), true)
  }

  const handleClose = () => {
    resetForm({ values: initialValues, touched: {}, errors: {} })
    setStates({ exampleAnchor: null, prevFileSelection: null, copyMapping: false })
    setOpen(false)
  }

  const tmplData = useSelector<ReduxState, FileEncryptionTemplateDto[]|undefined>(state => {
    var resStore = state.admin.resources['file-encrypt-template']
    return (resStore &&
      (resStore.list.ids ?? []).map(id => resStore.data[id])) as any
  })
  const tmplsForSelect = useMemo(() => {
    if (!states.copyMapping) { return [] }
    else {
      return (tmplData ?? []).filter(x => x.kind === values.kind)
        .sort((a, b) => (a.name || '').toUpperCase() < (b.name || '').toUpperCase() ? -1 : 1)
    }
  }, [states.copyMapping, values.kind, tmplData])
  
  return (
    <SwipeableDrawer
      anchor="right"
      onOpen={() => {}}
      onClose={handleClose}
      open={open}
      PaperProps={{ sx: { p: 2, width: width, }}}
    >
      <OdForm formikCtrl={formikCtrl}>

        <Card>
          <CardHeader title="Upload File Template" />
          <CardContent>
            <Grid container spacing={4} >
              <Grid item xs={12} >
                <TextField label="Template Name*" name="name" />
              </Grid>
              <Grid item xs={12} >
                <SelectField label="Template Type*" name="kind" >
                  <MenuItem value={FileTemplateKind.FixedWidth}>Fixed Width</MenuItem>
                  <MenuItem value={FileTemplateKind.CSV}>CSV</MenuItem>
                  <MenuItem value={FileTemplateKind.JsonSample}>Json Sample</MenuItem>
                  <MenuItem value={FileTemplateKind.EDI}>EDI</MenuItem>
                </SelectField>
              </Grid>
              {values.kind === FileTemplateKind.CSV &&
                <Grid item xs={12} >
                  <Stack direction="row" spacing={2}>
                    {/* <CharSelector id="select-delimiter-id" label="Delimiting Char" source="delimitingChar" options={[',', ';']} /> */}
                    {/* <CharSelector id="select-escape-id" label="Escape Char" source="escapeChar" options={['"', "'"]} /> */}
                    <CharSelector2 id="select-delimiter-id" label="Delimiting Char" source="delimitingChar" />
                  </Stack>
                </Grid>
              }
              <Grid item xs={12} >
                <FormControl 
                  fullWidth 
                  component="fieldset" 
                  error={Boolean(touched.templateFile && errors.templateFile)}
                >
                  <InputLabel htmlFor="template-file" shrink={true} >Template File*</InputLabel>
                  <OutlinedInput 
                    id="template-file" 
                    type="file" 
                    label="Template File*" 
                    notched={true}
                    name="templateFile"
                    value={values.templateFile}
                    onChange={handleTemplateFileChange}
                    inputProps={{ 
                      accept: values.kind === FileTemplateKind.JsonSample ? '.json, .txt' : '.csv, .txt',
                    }}
                  />
                  <FormHelperText>{touched.templateFile && errors.templateFile}</FormHelperText>
                  {Boolean(!touched.templateFile || !errors.templateFile) && (
                    values.kind === FileTemplateKind.CSV || values.kind === FileTemplateKind.FixedWidth || values.kind === FileTemplateKind.EDI ?
                      <>
                        <FormHelperText>
                          <b>Template Files</b> are expected to be CSV files that define data files later to be encrypted/decrypted. 
                          See <Link sx={{ cursor: 'pointer' }} onClick={(e:any) => setStates(v => ({...v, exampleAnchor: v.exampleAnchor ? null : e.target}))}>example</Link>.
                        </FormHelperText>
                        <Popover
                          id="template-example-pop"
                          open={!!states.exampleAnchor}
                          anchorEl={states.exampleAnchor}
                          onClose={() => {setStates(v => ({...v, exampleAnchor: null}))}}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                          }}
                          PaperProps={{ sx: { borderRadius: 0.5 } }}
                        >
                          <Box
                            sx={{
                              width: 600,
                              py: 2,
                              '& pre': {
                                mx: 1,
                                p: 1,
                                fontSize: '0.75em',
                                bgcolor: 'divider',
                                '& span.linenumber': {
                                  display: 'inline-block',
                                  minWidth: '1.25em',
                                  paddingRight: '1em',
                                  textAlign: 'right',
                                  userSelect: 'none',
                                  color: 'rgb(98, 114, 164)',
                                },
                              }
                            }}
                          >
                            <Typography sx={{ px: 2 }}>The example {
                                values.kind === FileTemplateKind.CSV        ? 'CSV File' 
                              : values.kind === FileTemplateKind.FixedWidth ? 'Fixed Width File' 
                              : 'EDI Encryption Definition File'
                            } template:</Typography>
                            {values.kind === FileTemplateKind.CSV ?
                              <>
                                <pre>
                                  <code>
                                    <span className="linenumber">1</span>
                                    <span>Column1,Column2,Column3</span><span>{'\r\n'}</span>
                                    <span className="linenumber">2</span>
                                    <span>...</span>
                                  </code>
                                </pre>
                                <Typography sx={{ px: 2 }} variant="caption" component="p">Note: only the header line matters; use comma ',' to separate each column name. </Typography>
                              </> :
                              values.kind === FileTemplateKind.FixedWidth ?
                              <>
                                <pre>
                                  <code>
                                    <span className="linenumber">1</span>
                                    <span>ColumnName,StartPosition,Length,DisplayName,Description</span><span>{'\r\n'}</span>
                                    <span className="linenumber">2</span>
                                    <span>FirstName,1,10,First Name,First Name - Alpha</span><span>{'\r\n'}</span>
                                    <span className="linenumber">3</span>
                                    <span>LastName,11,20,Last Name,Last Name - Alpha</span><span>{'\r\n'}</span>
                                    <span className="linenumber">4</span>
                                    <span>Dob,31,8,Date of Birth,Date of Birth - YYYYMMDD - Numeric</span><span>{'\r\n'}</span>
                                    <span className="linenumber">5</span>
                                    <span>...</span>
                                  </code>
                                </pre>
                                <Typography sx={{ px: 2 }} variant="caption" component="p">Note: "ColumnName", "StartPosition" and "Length" are required headers; "DisplayName" and "Description" are optional; use comma ',' to separate each column name; content of each field should not contain commas. </Typography>
                              </> :
                              values.kind === FileTemplateKind.EDI ?
                              <Box sx={{ '& pre': {overflowX: 'auto'} }}>
                                <pre>
                                  <code>
                                    <span className="linenumber">1</span>
                                    <span>Name,Segment,Anchor,Anchor Value,Element,Start,Length,Preceding Segment,Preceding Anchor,Preceding Anchor Value,Description</span><span>{'\r\n'}</span>
                                    <span className="linenumber">2</span>
                                    <span>Last Name,IN2,1,05,2,,,,,,</span><span>{'\r\n'}</span>
                                    <span className="linenumber">3</span>
                                    <span>First Name,IN2,1,02,2,,,,,,</span><span>{'\r\n'}</span>
                                    <span className="linenumber">4</span>
                                    <span>Middle Name,IN2,1,03,2,,,,,,</span><span>{'\r\n'}</span>
                                    <span className="linenumber">5</span>
                                    <span>...</span>
                                  </code>
                                </pre>
                                <Typography sx={{ px: 2 }} variant="caption" component="p">Note: Values for "Name", "Segment" and "Element" are required in each row of template data; use comma ',' to separate each column name; content of each field should not contain commas. </Typography>
                              </Box>
                              : null}
                          </Box>
                        </Popover>
                      </>
                    : values.kind === FileTemplateKind.JsonSample ?
                      <>
                        <FormHelperText>
                          <b>Template Files</b> are expected to be JSON sample files that define data files later to be encrypted/decrypted. The uploaded sample should be valid JSON format; data structures inside are expected to be consistent; sample data should not contain "null" values, although you are free to do that in files to be encrypted.
                        </FormHelperText>
                      </>
                    : null
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel label="Copy mapping from existing template"
                  control={
                    <Checkbox 
                      checked={states.copyMapping}
                      onChange={(e) => {
                        const copyMapping = e.target.checked
                        setStates(v => ({...v, copyMapping }))
                        if (!copyMapping) {
                          setValues(v => ({...v,
                            copyFrom: '',
                            includeObfs: false
                          }))
                        }
                      }}
                    />}
                />
              </Grid>
              {states.copyMapping &&
                <>
                  <Grid item xs={12}>
                    <SelectField label="Mapping Source Template" name="copyFrom">
                      {tmplsForSelect.map(t => (
                        <MenuItem key={t.templateId} value={t.templateId}>{t.name}</MenuItem>
                      ))}
                    </SelectField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel label="Include Obfuscation Rule Mapping"
                      control={
                        <Checkbox 
                          checked={values.includeObfs}
                          onChange={(e) => setFieldValue('includeObfs', e.target.checked)}
                        />}
                    />
                  </Grid>
                </>}
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }} >
            <Button
              color="primary"
              disabled={isSubmitting}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSubmitting}
              type="submit"
              variant="contained"
            >
              Upload
            </Button>
          </Box>
        </Card>

      </OdForm>
    </SwipeableDrawer>
  )
}

export default UploadFileTemplate;

const chipMap : Record<string, ReactElement> = {
  ',': <Chip label={<><Typography variant="body1" component="span" sx={{fontWeight:700}}>,</Typography></>} />,
  ';': <Chip label={<><Typography variant="body1" component="span" sx={{fontWeight:700}}>;</Typography></>} />,
  '\t': <Chip label={<>tab ( <Typography variant="body1" component="span" sx={{fontWeight:700}}>\t</Typography> )</>} />,
}

const CharSelector2 : FC<{
  id: string,
  label: string,
  source: string,
}> = ({ id, label, source }) => {
  const { values, setFieldValue } = useFormikContext<any>()
  return (
    <FormControl sx={{ width: 216 }} >
      <InputLabel id={`${id}-label`} >{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        renderValue={(selected:string) => (
          <Box sx={{ my: '-5px' }}>{chipMap[selected]}</Box>
        )}
        value={values[source] as string}
        onChange={(e) => { setFieldValue(source, e.target.value)}}
        input={<OutlinedInput id={`${id}-select`} label={label} />}
      >
        {Object.keys(chipMap).map(k => (
          <MenuItem key={k} value={k}>{chipMap[k]}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}


const CharSelector: FC<{
  id: string,
  options: string[],
  label: string,
  source: string,
}> = ({ label, source, ...rest }) => {
  const { values, setFieldValue } = useFormikContext<any>()
  return (
    <Autocomplete
      multiple
      limitTags={1}
      getOptionLabel={(option: any) => option}
      value={values[source]}
      renderInput={(params:any) => {
        const originOnChange = params.inputProps.onChange
        const onChange = (event: React.ChangeEvent<{value:string}>) => {
          const value = event.target.value
          event.target.value = [...value].pop() || ''
          originOnChange(event)
        }
        params.inputProps.onChange = onChange
        return (
          <TextField {...params} label={label} placeholder="Select/Type" />
        )
      }}
      onChange={(e:any, value, reason, details) => { 
        if (reason === 'createOption' || reason === 'selectOption') {
          setFieldValue(source, [details?.option]);
        }
      }}
      freeSolo={true}
      sx={{ 
        width: 216, 
        '& div.MuiInputBase-root': {
          '& span.MuiChip-label': {
            fontSize: '16px',
            fontWeight: 700,
          },
          '& svg.MuiSvgIcon-root': { display: 'none' },
        }
      }}
      {...rest}
    />
  )
}
